import { TableLoader } from 'components/TableLoader';
import { getColumnId, TableSettings } from 'components/TableSettings';
import { TotalCount } from 'components/TotalCount';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderResultViewData } from 'gql/graphql';
import { Grid, GridItem, Loader, Table } from 'uikit';

import type React from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getTagLabel } from '../workOrderConstants';
import { gqlWorkOrderResult } from '../workOrderQueries';

import { getSummaryTableAdditionalClassName } from './helpers';
import { useOrderSummaryTableColumns } from './hooks/useOrderSummaryTableColumns';
import { useOrderSummaryTableRows } from './hooks/useOrderSummaryTableRows';
import './WorkOrderSummaryTable.css';

interface IWorkOrderSummaryTable {
    costRounding: number;
}

export const WorkOrderSummaryTable: React.FC<IWorkOrderSummaryTable> = (props) => {
    const { costRounding } = props;
    const { orderId: workOrderId } = useParams();

    const variables = useMemo(() => ({ workOrderId: Number(workOrderId) }), [workOrderId]);

    const [workOrderResultData, workOrderResultStatus] = useRequest({
        requestId: 'workOrderResult',
        query: gqlWorkOrderResult,
        variables,
        withEffect: Boolean(workOrderId),
    });

    const workOrderResult = workOrderResultData?.uerWorkOrderResult;
    const isLoading = workOrderResultStatus === RequestStatus.FETCHING;
    const [columns, handleTableSettingsConfirm, isColumnVisible] = useOrderSummaryTableColumns(
        workOrderId as string,
        workOrderResult as WorkOrderResultViewData,
        costRounding,
    );

    const visibleColumns = useMemo(
        () => columns.filter((column) => isColumnVisible(getColumnId(column))),
        [columns, isColumnVisible],
    );
    const [rows, groupsCount] = useOrderSummaryTableRows(workOrderResult as WorkOrderResultViewData);

    return (
        <>
            <Grid cols={2} className="TableSettings">
                <GridItem>
                    <TotalCount totalCount={rows.length ? rows.length - groupsCount : 0} />
                </GridItem>
                <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                    <TableSettings
                        columns={columns}
                        onConfirm={handleTableSettingsConfirm}
                        isColumnVisible={isColumnVisible}
                    />
                </GridItem>
            </Grid>
            {isLoading && <Loader />}
            {!isLoading && !!rows.length && (
                <Table
                    columns={visibleColumns}
                    rows={rows}
                    getAdditionalClassName={getSummaryTableAdditionalClassName}
                    getTagLabel={getTagLabel}
                    size="l"
                    stickyHeader
                    className="workOrderSummaryTable"
                    borderBetweenColumns
                    emptyRowsPlaceholder={<TableLoader requestStatus={workOrderResultStatus} />}
                />
            )}
        </>
    );
};

WorkOrderSummaryTable.displayName = 'WorkOrderSummaryTable';
